import { ColumnDef } from '@tanstack/react-table';
import { Link, useParams } from 'react-router';

import { GetAssignmentsResponseDto } from '../../../dtos/responses/get-assignments.response.dto';
import { formatDate } from '../../../lib/date';
import { formatScore } from '../../../lib/score';
import { Button } from '../../ui/button';

export const ASSIGNMENT_COLUMNS: ColumnDef<GetAssignmentsResponseDto>[] = [
  {
    accessorKey: 'title',
    header: '과제명',
    cell: ({ row, table }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const phase = (table.options.meta as any).phase;
      if (phase === 'notStarted') {
        return <div>{row.original.title}</div>;
      }
      return <AssignmentLink id={row.original.id} title={row.original.title} />;
    },
    meta: { className: 'w-[8%]' },
  },
  {
    accessorKey: 'numProblems',
    header: '과제 문제수',
    cell: ({ row }) => <div className="text-left">{row.original.numProblems}문제</div>,
    meta: { className: 'w-[6%]' },
  },
  {
    accessorKey: 'totalScore',
    header: '총 배점',
    cell: ({ row }) => <div className="text-left">{formatScore(row.original.totalScore)}점</div>,
    meta: { className: 'w-[6%]' },
  },
  {
    accessorKey: 'submitScore',
    header: '수정후점수',
    cell: ({ row }) => (
      <div className="text-left">
        {formatScore(row.original.adjustedScore ?? row.original.submitScore ?? 0)}점
      </div>
    ),
    meta: { className: 'w-[6%]' },
  },
  {
    accessorKey: 'submitScorePercentile',
    header: '수정후점수(%)',
    cell: ({ row }) => {
      if (row.original.totalScore == null || row.original.totalScore === 0) {
        return <div>-</div>;
      }

      const score = row.original.adjustedScore ?? row.original.submitScore;
      if (score == null) {
        return <div>-</div>;
      }

      return (
        <div className="text-left">{formatScore((score / row.original.totalScore) * 100)}%</div>
      );
    },

    meta: { className: 'w-[6%]' },
  },
  {
    accessorKey: 'maxSubmissionCount',
    header: '총 학생시도 허용횟수',
    cell: ({ row }) => <div className="text-left">{row.original.maxSubmissionCount}회</div>,
    meta: { className: 'w-[6%]' },
  },
  {
    accessorKey: 'explanationDisplayStrategy',
    header: '해설 노출 방식',
    cell: ({ row }) => (
      <div className="text-left">
        {row.original.explanationDisplayStrategy === 'AFTER_FINAL_SUBMISSION' && '마지막 문제 제출 후'}
        {row.original.explanationDisplayStrategy === 'AFTER_EACH_SUBMISSION' && '각 문제 제출 시'}
        {row.original.explanationDisplayStrategy === 'AFTER_DEADLINE' && '과제 기간 종료 후'}
      </div>
    ),
    meta: { className: 'w-[8%]' },
  },
  {
    accessorKey: 'updatedAt',
    header: '제출 일시',
    cell: ({ row }) => <div className="text-left">{formatDate(row.original.updatedAt)}</div>,
    meta: { className: 'w-[10%]' },
  },
  {
    accessorKey: 'startAt',
    header: '시작 일시',
    cell: ({ row }) => <div>{formatDate(row.original.startAt)}</div>,
    meta: { className: 'w-[10%]' },
  },
  {
    accessorKey: 'endAt',
    header: '마감 일시',
    // 개인 마감연장이 있으면 실효 마감을 보여주고 "연장" 배지 표시(연장 사실을 학생에게 안내). UX-03
    cell: ({ row }) => {
      const { endAt, effectiveEndAt } = row.original;
      const isExtended =
        new Date(effectiveEndAt).getTime() > new Date(endAt).getTime();
      return (
        <div className="flex items-center gap-1">
          <span>{formatDate(effectiveEndAt)}</span>
          {isExtended && (
            <span className="shrink-0 text-[10px] font-semibold text-orange-600 bg-orange-100 rounded px-1 py-0.5">
              연장
            </span>
          )}
        </div>
      );
    },
    meta: { className: 'w-[10%]' },
  },
  {
    accessorKey: 'actions',
    header: '과제 풀기',
    cell: ({ row, table }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const phase = (table.options.meta as any).phase;
      return <SolveAssignment id={row.original.id} phase={phase} />;
    },
    meta: { className: 'w-[5%] text-center' },
  },
];

interface AssignmentLinkProps {
  id: string;
  title: string;
}

const AssignmentLink = (props: AssignmentLinkProps) => {
  const { id, title } = props;
  const { courseId } = useParams<{ courseId: string }>();
  return (
    <Link
      className="underline underline-offset-2 text-[#0085ff] cursor-pointer"
      to={`/course/${courseId}/${id}`}
    >
      {title}
    </Link>
  );
};

interface SolveAssignmentProps {
  id: string;
  phase: string;
}

const SolveAssignment = (props: SolveAssignmentProps) => {
  const { id, phase } = props;
  const { courseId } = useParams<{ courseId: string }>();

  // Disable button if not started or completed (deadline passed)
  const disabled = phase === 'notStarted' || phase === 'completed';

  return (
    <div className="text-center">
      <Button type="button" variant={'default'} disabled={disabled} size={'sm'}>
        <Link to={`/course/${courseId}/${id}/solve`}>과제 풀기</Link>
      </Button>
    </div>
  );
};
